import React from "react";
import {Button, Chip, Dialog, DialogContent, DialogTitle, IconButton} from "@mui/material";
import {Close} from "@mui/icons-material";
import {BRIEFING, CONFIDENCE_DIMENSIONS} from "../briefingData";
import type {ConfidenceDimension} from "../briefingTypes";
import {formatCompactEuro} from "../briefingLogic";
import {useBriefing} from "../briefingState";

type Props = {
    open: boolean
    onClose: () => void
};

/**
 * The opportunity that needs confirmation: names the weak dimension, the
 * blocker, the owner, and the small human action that fixes it. Asking the
 * owner parks the confidence — it renders as waiting, not as low.
 */
export const NeedsConfirmationDialog: React.FC<Props> = ({open, onClose}) => {
    const {state, view, dispatch} = useBriefing();
    const move = BRIEFING.needs_confirmation[0];
    if (!move) return null;

    const valueTypeLabel = BRIEFING.impact_discipline.types.find(t => t.id === move.potential_impact_type)?.label ?? move.potential_impact_type;
    const threshold = BRIEFING.ready_to_act_gate.confidence_threshold;
    const classMeta = BRIEFING.confidence_model.dimensions;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16, fontWeight: 600, pr: 6}}>
                {move.title}
                <IconButton size="small" aria-label="Close" onClick={onClose} sx={{position: 'absolute', right: 12, top: 12}}>
                    <Close fontSize="small"/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <div className="db-nc-meta">
                    Potential impact: <b>{formatCompactEuro(move.potential_impact)}</b> {valueTypeLabel.toLowerCase()} · confidence <b>{move.confidence}%</b> — under the {threshold} gate, so not ready to act
                </div>

                <div className="db-nc-classes">
                    {CONFIDENCE_DIMENSIONS.map(cls => {
                        const isWeakest = cls === move.weakest_dimension;
                        return (
                            <div key={cls} className={"db-dim-row" + (isWeakest ? " lowest" : "")}>
                                <span className="db-dim-label">{classMeta.find(c => c.id === cls)?.label ?? cls}</span>
                                <span className="db-dim-value">
                                    {move.dimension_confidences[cls]}%
                                    {isWeakest && <span className="db-min-tag">needs confirmation</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div className="db-nc-blocker">
                    <div className="db-panel-section-title">Blocker</div>
                    <div>{move.blocker.statement}</div>
                    <div className="db-fact-meta">
                        {BRIEFING.tenant.source_systems.find(s => s.id === move.blocker.source_system)?.label ?? move.blocker.source_system}
                        {' '}· <span className="db-fact-record">{move.blocker.record_id}</span> · {move.blocker.owner}
                    </div>
                </div>

                <div className="db-nc-resolution">
                    {state.blockedOwnerAsked
                        ? <span className="db-fact-parked">
                            Waiting on {move.blocker.owner} — confidence parked at {move.confidence} until they answer.
                            When it lands: {move.confidence} + {move.blocker.resolution.expected_confidence_gain} = {move.confidence + move.blocker.resolution.expected_confidence_gain},
                            which clears the gate at {threshold} — the opportunity becomes ready to act.
                        </span>
                        : <>
                            <Button variant="contained" size="small" disableElevation onClick={() => dispatch({type: 'askBlockedOwner'})}>
                                {move.blocker.resolution.label}
                            </Button>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${move.blocker.resolution.effort_minutes} min · +${move.blocker.resolution.expected_confidence_gain} confidence`}
                            />
                        </>
                    }
                </div>

                {view.needsConfirmation.length > 0 &&
                    <div className="db-nc-gated">
                        <div className="db-panel-section-title">Needs confirmation after an override</div>
                        {view.needsConfirmation.map(c => {
                            const weakest = CONFIDENCE_DIMENSIONS.reduce((min, cls) =>
                                c.dimensionConfidences[cls] < c.dimensionConfidences[min] ? cls : min, 'scope' as ConfidenceDimension);
                            const weakLabel = BRIEFING.confidence_model.dimensions.find(m => m.id === weakest)?.label ?? weakest;
                            const override = (c.opportunity.assumptions ?? []).map(a => ({a, o: state.assumptionOverrides[a.id]})).find(x => x.o);
                            return (
                                <div key={c.opportunity.id} className="db-fact-item">
                                    <div className="db-fact-claim">{c.opportunity.title}</div>
                                    <div className="db-fact-meta">
                                        {c.opportunity.subject} · {weakLabel} {c.dimensionConfidences[weakest]}% — under the {threshold} gate
                                        {override && <> · overridden: “{override.o!.reason}”</>}
                                    </div>
                                    {override &&
                                        <div className="db-fact-affordance">
                                            <Button size="small" className="db-fact-action" onClick={() => dispatch({type: 'resetAssumption', id: override.a.id})}>
                                                Reset {override.a.label.toLowerCase()} — the opportunity becomes ready to act again
                                            </Button>
                                        </div>
                                    }
                                </div>
                            );
                        })}
                    </div>
                }
            </DialogContent>
        </Dialog>
    );
};
